import { useFragment } from '@apollo/client';
import {
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CSSObject,
  IconButton,
  Stack,
  styled,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { friendlyPhoneFormat } from '@provision/chat-utils';
import { ReactNode, RefObject, useMemo, useRef, useState } from 'react';
import { mapValues } from 'remeda';
import { DynamicIcon, Tooltip, WithTooltip } from 'src/components';
import { ClientMenu } from 'src/components/ClientComponents/client-menu';
import { ClientName } from 'src/components/ClientComponents/client-name';
import { useCardCustomAttributeRows } from 'src/components/CustomAttributes/use-card-custom-attribute-rows';
import { CONTAINER_CLASS } from 'src/components/Fields/LabelField';
import { ShowMoreButton } from 'src/components/ShowMoreButton/show-more-button';
import { gql } from 'src/gql';
import {
  Address,
  Conversation,
  useAddress,
  useCtaLabels,
  usePhonesAndEmailsLabels,
} from 'src/graphql';
import {
  CLIENT_CARD_PROPERTY_KEYS,
  useSectionVisibility,
  useSidePanelSectionOrder,
} from 'src/graphql/entities/side-panel-personalization';
import { ClientRaw } from 'src/types';
import { useCopyToClipboard, useNotifications } from 'src/utils/hooks';
import { ClientLabel } from './client-label';
import { WithCollapse } from './site-clients';

export const StyledCardHeader = styled(CardHeader, {
  name: 'StyledCardHeader',
})(({ theme }) => ({
  alignSelf: 'center',
  borderBottom: '1px solid',
  borderColor: theme.palette.custom.OV.sys.outlineVariant,
  padding: theme.spacing(0.5, 2),

  '& .MuiCardHeader-action': {
    alignSelf: 'center',
  },
}));

export const StyledTypography = styled(Typography, {
  name: 'StyledTypography',
  shouldForwardProp: (prop) => prop !== 'allowMultipleLines',
})<{
  allowMultipleLines: boolean;
}>(({ allowMultipleLines }) => {
  if (allowMultipleLines) {
    return {
      whiteSpace: 'pre-wrap',
      flexWrap: 'wrap',

      '& p:not(style)': {
        margin: 0,
      },
    };
  }

  return {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    flexWrap: 'nowrap',
  };
});

const CLIENT_NOTE_HEIGHT_COLLAPSED = 60;

interface ClientItem {
  label?: string;
  icon?: string;
  action?: {
    text: string;
    onClick: () => void;
  };
  ref?: RefObject<HTMLDivElement | null>;
  centerVertically?: boolean;
  text?: ReactNode;
  clickToCopy?: boolean;
  allowDangerousHTML?: boolean;
  allowMultipleLines?: boolean;
  collapseSize?: string;
  skip?: boolean;
  /** rendered as-is instead of the icon + text row (custom attributes bring their own row) */
  node?: ReactNode;
}

const StyledTextContainer = styled(Stack, {
  name: 'StyledTextContainerTypography',
  shouldForwardProp: (prop) => prop !== 'clientItem' && prop !== 'isOpen',
})<{
  isOpen?: boolean;
  clientItem: ClientItem;
}>(({ theme, clientItem, isOpen }) => {
  const styles = {
    display: 'flex',
    gap: theme.spacing(0.5),
    cursor: clientItem.clickToCopy !== false ? 'pointer' : undefined,
    position: 'relative',
  } as CSSObject;

  if (
    !isOpen &&
    (clientItem.ref?.current?.clientHeight ?? 0) >= CLIENT_NOTE_HEIGHT_COLLAPSED
  ) {
    styles['&::after'] = {
      content: '""',
      background: `linear-gradient(180deg, hsla(0, 0%, 100%, 0) 0, ${theme.palette.custom.OV.sys.surfaceContainerLowest})`,
      backgroundImage: `linear-gradient(rgba(255, 255, 255, 0) 0px, ${theme.palette.custom.OV.sys.surfaceContainerLowest})`,
      height: '4rem',
      width: '100%',
      position: 'absolute',
      left: 0,
      top: 0,
    };
  }

  return styles;
});

const ClientCardFragment = gql(`
  fragment ClientCard_Client on Client {
    id
    clientId
    partnerId
    firstName
    lastName
    fullName @client
    isDeleted @client
    deletedAt
    notes
    primaryEmail {
      id
      value
      labelId
    }
    primaryPhoneNumber {
      id
      value
      labelId
    }
    emails {
      id
      value
      labelId
    }
    phoneNumbers {
      id
      value
      labelId
    }
    ...ClientMenu_Client
  }
`);

/** A built-in card row: icon, copyable text with its label, collapsing for long notes. */
const ClientItemRow = ({
  clientItem,
  isOpen,
}: {
  clientItem: ClientItem;
  isOpen: boolean;
}) => {
  const theme = useTheme();
  const { copyToClipboard } = useCopyToClipboard();

  return (
    <WithCollapse
      showCollapse={
        (!!clientItem.collapseSize && !!clientItem.text && !clientItem.ref?.current) ||
        (clientItem.ref?.current?.clientHeight ?? 0) >= CLIENT_NOTE_HEIGHT_COLLAPSED
      }
      in={isOpen}
      collapsedSize={clientItem.collapseSize}
    >
      <Stack
        direction="row"
        alignItems={clientItem.centerVertically ? 'center' : undefined}
        gap={1}
        py={1}
        className={CONTAINER_CLASS}
      >
        <DynamicIcon name={clientItem.icon ?? ''} library="lucide" size={21} />
        {!!clientItem.text && (
          <Stack direction="row" flexWrap="wrap" overflow="hidden">
            <WithTooltip
              hideWhenRefIsHidden
              hideTooltip={clientItem.clickToCopy === false}
              title={
                <Stack direction="column" alignItems="center">
                  <span>{clientItem.text}</span>
                  <span>Click to copy</span>
                </Stack>
              }
            >
              <StyledTextContainer
                clientItem={clientItem}
                isOpen={isOpen}
                ref={clientItem?.ref}
                direction="row"
                overflow="hidden"
              >
                {typeof clientItem.text === 'string' ? (
                  <StyledTypography
                    variant="body2"
                    allowMultipleLines={!!clientItem.allowMultipleLines}
                    dangerouslySetInnerHTML={
                      clientItem.allowDangerousHTML
                        ? {
                            __html: clientItem.text,
                          }
                        : undefined
                    }
                    onClick={
                      clientItem.clickToCopy !== false
                        ? async () => {
                            if (typeof clientItem.text === 'string' && clientItem.text) {
                              await copyToClipboard(clientItem.text);
                            }
                          }
                        : undefined
                    }
                  >
                    {!clientItem.allowDangerousHTML ? clientItem.text : undefined}
                  </StyledTypography>
                ) : (
                  clientItem.text
                )}

                {!!clientItem.label && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.custom.OV.sys.onSurfaceVariant,
                    }}
                  >
                    ({clientItem.label.trim()})
                  </Typography>
                )}
              </StyledTextContainer>
            </WithTooltip>
          </Stack>
        )}

        {!clientItem.text && !clientItem.action && <Typography>-</Typography>}
      </Stack>
    </WithCollapse>
  );
};

export const ClientCard = ({
  clientId,
  address,
  conversation,
  sx,
  showClientMenu = true,
  clientNameTooltipText = '',
  onClientNameClick,
  showMoreButton = true,
  headerAction,
  hideHeader = false,
  disabled = false,
}: {
  clientId: string;
  address?: Partial<Address>;
  conversation?: Conversation;
  sx?: SxProps<Theme>;
  showClientMenu?: boolean;
  showMoreButton?: boolean;
  clientNameTooltipText?: string;
  onClientNameClick?: (clientId: string) => void;
  headerAction?: ReactNode;
  /** Skip the card's own name row when a host (e.g. a SidePanelCard header) already shows it as its title. */
  hideHeader?: boolean;
  /** locks the custom attributes — a closed or deleted conversation host */
  disabled?: boolean;
}) => {
  const theme = useTheme();
  const { showSuccessAlert } = useNotifications();
  const [isOpen, setIsOpen] = useState(!showMoreButton);

  const { data: client, complete } = useFragment({
    fragment: ClientCardFragment,
    fragmentName: 'ClientCard_Client',
    from: {
      __typename: 'Client',
      id: clientId,
    },
  });

  const { updateAddress } = useAddress(address?.addressId);

  const { copyToClipboard } = useCopyToClipboard();

  const { ctaLabels } = useCtaLabels(clientId, address?.addressId);

  const clientEmailsWithoutPrimary = useMemo(
    () =>
      client.primaryEmail?.value
        ? client.emails?.filter((email) => email?.value !== client.primaryEmail?.value)
        : (client.emails?.slice(1, client.emails.length) ?? []),
    [client.emails, client.primaryEmail],
  );

  const clientPhonesWithoutPrimary = useMemo(
    () =>
      client.primaryPhoneNumber?.value
        ? client.phoneNumbers?.filter(
            (phoneNumber) =>
              friendlyPhoneFormat(phoneNumber?.value ?? undefined) !==
              friendlyPhoneFormat(client.primaryPhoneNumber?.value ?? undefined),
          )
        : (client.phoneNumbers?.slice(1, client.phoneNumbers.length) ?? []),
    [client.phoneNumbers, client.primaryPhoneNumber],
  );

  const notesRef = useRef<HTMLDivElement>(null);

  const { clientEmailLabels, clientPhoneLabels } = usePhonesAndEmailsLabels();

  const isVisible = useSectionVisibility('client');
  const sectionOrder = useSidePanelSectionOrder('client');

  const customAttributeRows = useCardCustomAttributeRows({
    sectionKey: 'client',
    entity: 'Client',
    entityId: clientId,
    partnerId: client.partnerId,
    disabled: disabled || !!client.isDeleted,
  });

  const clientItems = useMemo(() => {
    const primaryEmailText = client.primaryEmail?.value || client.emails?.[0]?.value?.trim();
    const primaryPhoneText = friendlyPhoneFormat(
      client.primaryPhoneNumber?.value || client.phoneNumbers?.[0]?.value,
    );

    const itemsByKey: Record<string, ClientItem[]> = {
      [CLIENT_CARD_PROPERTY_KEYS.email]: [
        {
          icon: 'mail',
          text: primaryEmailText,
          label: client.primaryEmail?.value
            ? clientEmailLabels?.find(
                (emailLabel) => emailLabel.id === client.primaryEmail?.labelId,
              )?.label
            : clientEmailLabels?.find(
                (emailLabel) => emailLabel.id === client.emails?.[0]?.labelId,
              )?.label,
          centerVertically: true,
          skip: !isVisible(CLIENT_CARD_PROPERTY_KEYS.email, !primaryEmailText),
        },
        // If for some reason there is no primaryEmail but there is an element
        // in the array, it will be considered as the primaryEmail
        ...(clientEmailsWithoutPrimary?.map((email) => ({
          text: email?.value?.trim(),
          label: clientEmailLabels?.find((emailLabel) => emailLabel.id === email?.labelId)
            ?.label,
          centerVertically: true,
          collapseSize: '0',
          skip: !isVisible(CLIENT_CARD_PROPERTY_KEYS.email, !email?.value?.trim()),
        })) ?? []),
      ],
      [CLIENT_CARD_PROPERTY_KEYS.phone]: [
        {
          icon: 'phone',
          text: primaryPhoneText,
          label: client.primaryPhoneNumber?.value
            ? clientPhoneLabels?.find(
                (phoneLabel) => phoneLabel.id === client.primaryPhoneNumber?.labelId,
              )?.label
            : clientPhoneLabels?.find(
                (phoneLabel) => phoneLabel.id === client.phoneNumbers?.[0]?.labelId,
              )?.label,
          centerVertically: true,
          skip: !isVisible(CLIENT_CARD_PROPERTY_KEYS.phone, !primaryPhoneText),
        },
        // If for some reason there is no primaryPhone but there is an element
        // in the array, it will be considered as the primaryPhone
        ...(clientPhonesWithoutPrimary?.map((phone) => ({
          text: friendlyPhoneFormat(phone?.value ?? undefined),
          label: clientPhoneLabels?.find((phoneLabel) => phoneLabel.id === phone?.labelId)
            ?.label,
          centerVertically: true,
          collapseSize: '0',
          skip: !isVisible(
            CLIENT_CARD_PROPERTY_KEYS.phone,
            !friendlyPhoneFormat(phone?.value),
          ),
        })) ?? []),
      ],
      [CLIENT_CARD_PROPERTY_KEYS.labels]: [
        {
          icon: 'tags',
          text: (
            <ClientLabel
              labels={ctaLabels}
              clientId={clientId}
              isDeleted={client.isDeleted}
              address={address}
              partnerId={address?.partner?.partnerId ?? address?.partnerId}
            />
          ),
          skip:
            !address?.addressId ||
            !isVisible(CLIENT_CARD_PROPERTY_KEYS.labels, !ctaLabels?.length),
          clickToCopy: false,
        },
      ],
      [CLIENT_CARD_PROPERTY_KEYS.note]: [
        {
          icon: 'sticky-note',
          text: client.notes,
          clickToCopy: false,
          collapseSize: `${CLIENT_NOTE_HEIGHT_COLLAPSED + 8}px`,
          ref: notesRef,
          allowDangerousHTML: true,
          allowMultipleLines: true,
          skip: !isVisible(CLIENT_CARD_PROPERTY_KEYS.note, !client.notes),
        },
      ],
      ...mapValues(customAttributeRows, (node) => [{ node }]),
    };

    // the personalized layout orders the properties; the record's own key
    // order is the built-in fallback when no personalization applies
    const orderedKeys = sectionOrder ?? Object.keys(itemsByKey);

    return orderedKeys
      .flatMap((key) => itemsByKey[key] ?? [])
      .filter((option) => !option.skip);
  }, [
    client.primaryEmail?.value,
    client.primaryEmail?.labelId,
    client.emails,
    client.primaryPhoneNumber?.value,
    client.primaryPhoneNumber?.labelId,
    client.phoneNumbers,
    client.isDeleted,
    client.notes,
    clientEmailLabels,
    isVisible,
    clientEmailsWithoutPrimary,
    clientPhoneLabels,
    clientPhonesWithoutPrimary,
    ctaLabels,
    clientId,
    address,
    sectionOrder,
    customAttributeRows,
  ]);

  const showShowMoreButton =
    showMoreButton &&
    ((isVisible(CLIENT_CARD_PROPERTY_KEYS.phone) && (client.phoneNumbers?.length ?? 0) > 1) ||
      (isVisible(CLIENT_CARD_PROPERTY_KEYS.email) && (client.emails?.length ?? 0) > 1) ||
      (notesRef?.current?.clientHeight ?? 0) >= CLIENT_NOTE_HEIGHT_COLLAPSED);

  if (!complete) {
    return null;
  }

  return (
    <Card
      sx={{
        border: '1px solid',
        borderColor: theme.palette.custom.OV.sys.outlineVariant,
        ...(sx ?? {}),
      }}
    >
      {hideHeader ? null : (
        <StyledCardHeader
          sx={{
            '& .MuiCardHeader-content': {
              overflow: 'hidden',
            },
          }}
          title={
            <Stack direction="row" alignItems="center">
              <ClientName
                fullName={client.fullName}
                tooltipText={clientNameTooltipText}
                onClick={() => onClientNameClick?.(clientId)}
              />
              {!!conversation || !!address ? (
                <Tooltip
                  title={
                    <Stack direction="column" alignItems="center">
                      <span>{client.fullName}</span>
                      <span>Click to copy</span>
                    </Stack>
                  }
                >
                  <IconButton
                    aria-label="settings"
                    onClick={async () => {
                      await copyToClipboard(client.fullName);
                    }}
                  >
                    <DynamicIcon name="copy" library="lucide" />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
          }
          titleTypographyProps={{
            variant: 'subtitle2',
          }}
          action={
            <>
              {headerAction}
              {!!address?.addressId && (
                <WithTooltip
                  hideTooltip={address?.primaryClientId === clientId}
                  title="Set primary client"
                >
                  <IconButton
                    onClick={async () => {
                      const updated = await updateAddress(
                        {
                          ...address,
                          primaryClientId: clientId,
                          // TODO(gql-migrate): useAddress#updateAddress still expects a legacy
                          // raw client for its optimistic `primaryClient` payload.
                          primaryClient: {
                            id: client.id,
                            clientId,
                            partnerId: client.partnerId,
                            firstName: client.firstName,
                            lastName: client.lastName,
                          } as Partial<ClientRaw>,
                        },
                        conversation?.conversationId,
                      );

                      if (!updated?.[0]) {
                        return;
                      }
                      showSuccessAlert('Primary Client Updated');
                    }}
                    disabled={address?.primaryClientId === clientId || client.isDeleted}
                  >
                    <DynamicIcon
                      name="star"
                      library="lucide"
                      color={address?.primaryClientId === clientId ? '#F6A609' : undefined}
                      sx={{
                        '& svg': {
                          fill:
                            address?.primaryClientId === clientId ? 'currentColor' : 'none',
                        },
                      }}
                    />
                  </IconButton>
                </WithTooltip>
              )}
              {!!showClientMenu && (
                <ClientMenu
                  clientId={clientId}
                  address={address}
                  conversation={conversation}
                />
              )}
            </>
          }
        />
      )}
      <CardContent
        sx={{
          padding: theme.utils.important(theme.spacing(1)),
        }}
      >
        {clientItems.map(
          (clientItem) =>
            clientItem.node ?? (
              <ClientItemRow
                key={
                  (typeof clientItem.text === 'string' ? clientItem.text : '') +
                  (clientItem.icon ?? '')
                }
                clientItem={clientItem}
                isOpen={isOpen}
              />
            ),
        )}
      </CardContent>
      {showShowMoreButton ? (
        <CardActions
          sx={{
            borderTop: '1px solid',
            borderColor: theme.palette.custom.OV.sys.outlineVariant,
          }}
        >
          <ShowMoreButton
            showShowMoreButton={showShowMoreButton}
            sx={{
              color: theme.palette.text.secondary,
              width: '100%',
            }}
            endIcon={
              <DynamicIcon
                name="chevron-down"
                library="lucide"
                sx={{
                  rotate: isOpen ? '180deg' : undefined,
                  transition: 'rotate 300ms',
                }}
              />
            }
            onClick={() => setIsOpen((currentValue) => !currentValue)}
            text={isOpen ? 'Show Less' : 'Show More'}
          />
        </CardActions>
      ) : null}
    </Card>
  );
};
